import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../utils/supabase';
import cardDetails from '../components/assets/information/CardDetails';
import { useLocation, useNavigate } from 'react-router-dom';
import { Listbox } from '@headlessui/react';
import { v4 as uuidv4 } from 'uuid'; // Used for generating guestUIDs
import { WandsIcon, CupsIcon, SwordsIcon, PentaclesIcon, MajorArcanaIcon } from '../components/assets/images/SuitIcons';
import DarkModeToggle from '../components/DarkModeToggle';


const courtCardImages = {
 King: '/king.svg',
 Queen: '/queen.svg',
 Knight: '/knight.svg',
 Page: '/page.svg',
};


const numberMapping = {
 Ace: "A",
 Two: "2",
 Three: "3",
 Four: "4",
 Five: "5",
 Six: "6",
 Seven: "7",
 Eight: "8",
 Nine: "9",
 Ten: "10",
};



const ReadingSession = () => {
 const location = useLocation();
 const navigate = useNavigate();
 const selectedReadingType = location.state?.selectedReadingType;
 
  // States
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSuit, setSelectedSuit] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [notes, setNotes] = useState({});
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [stepSelections, setStepSelections] = useState({});
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(true);
  const [readingQuestion, setReadingQuestion] = useState('');
  const [guestUID, setGuestUID] = useState(localStorage.getItem('guestUID') || null);
  const [isReversed, setIsReversed] = useState(false);
  const { stepAction, stepMeaning, stepMap } = steps[currentStep] || {};
  const cardDetailsForSuit = selectedSuit ? cardDetails[selectedSuit].cards : [];
  const cardDetailsForSelectedCard = cardDetailsForSuit.find((card) => card.name === selectedCard);
    


// Generate guest UID if not already set
useEffect(() => {
  if (!guestUID) {
    const newGuestUID = uuidv4();
    localStorage.setItem('guestUID', newGuestUID);
    setGuestUID(newGuestUID);
  }
}, [guestUID]);

 // Redirect to Home if `selectedReadingType` is not set
 useEffect(() => {
   if (!selectedReadingType) navigate('/');
 }, [selectedReadingType, navigate]);

  // Fetch Steps Data
  useEffect(() => {
    const fetchSteps = async () => {
      try {
        const { data, error } = await supabase
          .from('reading_type')
          .select('steps','name')
          .eq('id', selectedReadingType);
 
 
        if (error) console.error('Error fetching steps:', error);
        else if (data.length > 0) setSteps(data[0].steps || []);
      } catch (err) {
        console.error('Unexpected error:', err);
      }
    };
 
    fetchSteps();
  }, [selectedReadingType]);
  
  //save reading q 
  const saveReadingQuestion = async () => {
    try {
      console.log('Saving reading question for guestUID:', guestUID);
      console.log('Reading question:', readingQuestion);
      console.log('Reading type:', selectedReadingType);
      
      // First check if session already exists
      const { data: existingSessions, error: checkError } = await supabase
        .from('guest_sessions')
        .select('id')
        .eq('guest_uid', guestUID);

      if (checkError) {
        console.error('Error checking for existing session:', checkError);
        alert('Failed to check existing session. Please try again.');
        return false;
      }

      let result;
      if (existingSessions && existingSessions.length > 0) {
        // Update existing session
        console.log('Updating existing session');
        result = await supabase
          .from('guest_sessions')
          .update({
            reading_question: readingQuestion,
            reading_type: selectedReadingType,
          })
          .eq('guest_uid', guestUID);
      } else {
        // Insert new session
        console.log('Creating new session');
        result = await supabase.from('guest_sessions').insert([
          {
            guest_uid: guestUID,
            reading_question: readingQuestion,
            reading_type: selectedReadingType,
          },
        ]);
      }

      if (result.error) {
        console.error('Error saving reading question:', result.error);
        alert(`Database error: ${result.error.message}`);
        return false;
      }

      console.log('Reading question saved successfully');
      return true;
    } catch (error) {
      console.error('Unexpected error saving reading question:', error);
      alert('Failed to save your question. Please try again.');
      return false;
    }
  };

  const handleSaveQuestionAndCloseModal = async () => {
    if (!readingQuestion.trim()) {
      alert('Please enter a question before proceeding.');
      return;
    }
  
    const success = await saveReadingQuestion();
    if (success) {
      setIsQuestionModalOpen(false);
    }
  };

 
 const handleSuitSelection = (suitKey) => {
   setSelectedSuit(suitKey);
   setSelectedCard(null); // Reset card selection on suit change
 };


 const handleCardSelection = (value) => {
   setSelectedCard(value);
 };
  const handleExit = () => {
   navigate('/'); // Redirect to the home page
 };


 const handleNoteChange = (event) => {
   setNotes((prev) => ({
     ...prev,
     [currentStep]: event.target.value,
   }));
 };

 const handleNextStep = async () => {
  if (!selectedSuit || !selectedCard) {
    alert("Please select both a suit and a card to proceed.");
    return;
  }

  const currentStepData = {
    step: currentStep + 1,
    stepMeaning,
      stepMap,
      selectedSuit,
      selectedCard,
      isReversed,
      cardDetailsForSelectedCard: {
        ...cardDetailsForSelectedCard,
        suit: cardDetailsForSelectedCard?.suit || cardDetails[selectedSuit]?.name || cardDetails[selectedSuit]?.icon,
      },
      notes: notes[currentStep] || '',
  };

  setStepSelections((prevSelections) => ({
    ...prevSelections,
    [currentStep]: currentStepData,
  }));

  try {
    console.log('Looking for session with guestUID:', guestUID);
    
    const { data: sessionData, error: fetchError } = await supabase
      .from('guest_sessions')
      .select('id, step_data')
      .eq('guest_uid', guestUID)
      .limit(1)
      .single();

    if (fetchError) {
      console.error('Error fetching session data:', fetchError.message);
      console.error('This usually means no session was found for guestUID:', guestUID);
      alert('No reading session found. Please start by entering your reading question first.');
      return;
    }

    console.log('Found session:', sessionData);

    const updatedStepData = sessionData?.step_data
      ? [...sessionData.step_data, currentStepData]
      : [currentStepData];

    const { error: saveError } = await supabase
      .from('guest_sessions')
      .update({
        step_data: updatedStepData,
      })
      .eq('id', sessionData.id); 

    if (saveError) {
      console.error('Error saving step data:', saveError.message);
      alert('An error occurred while saving this step.');
      return;
    }
  } catch (err) {
    console.error('Unexpected error:', err);
    alert('An unexpected error occurred. Please try again.');
    return;
  }

  if (currentStep < steps.length - 1) {
    setCurrentStep((prevStep) => prevStep + 1);
    const nextStepSelections = stepSelections[currentStep + 1] || {};
    setSelectedSuit(nextStepSelections.selectedSuit || null);
    setSelectedCard(nextStepSelections.selectedCard || null);
    setIsReversed(nextStepSelections.isReversed || false);
  }
};


const handlePreviousStep = () => {
  if (currentStep > 0) {
    setCurrentStep((prevStep) => prevStep - 1);

    // Retrieve the previous step selections if available
    const previousStepSelections = stepSelections[currentStep - 1] || {};
    setSelectedSuit(previousStepSelections.selectedSuit || null);
    setSelectedCard(previousStepSelections.selectedCard || null);
    setIsReversed(previousStepSelections.isReversed || false);
    setNotes((prevNotes) => ({
      ...prevNotes,
      [currentStep - 1]: previousStepSelections.notes || '',
    }));
  }
};

const handleFinaliseReading = async () => {
  if (!selectedSuit || !selectedCard) {
    alert('Please complete the final step before finalising.');
    return;
  }

  const combinedStepData = {
    ...stepSelections,
    [currentStep]: {
      step: currentStep + 1,
      stepMeaning,
      stepMap,
      selectedSuit,
      selectedCard,
      isReversed,
      cardDetailsForSelectedCard: {
        ...cardDetailsForSelectedCard,
        suit: cardDetailsForSelectedCard?.suit || cardDetails[selectedSuit]?.name || cardDetails[selectedSuit]?.icon,
      },
      notes: notes[currentStep] || '',
    },
  };

  const finalStepData = Object.keys(combinedStepData).map((step) => ({
    step: parseInt(step, 10),
    ...combinedStepData[step],
  }));

  try {
    const uniqueId = uuidv4();
    console.log('Generated unique URL ID:', uniqueId);
    
    if (!uniqueId) {
      console.error('Failed to generate unique ID!');
      alert('Failed to generate unique URL. Please try again.');
      return;
    }

    // Check if session exists first
    console.log('Checking for existing session with guestUID:', guestUID);
    
    const { data: existingSession, error: checkError } = await supabase
      .from('guest_sessions')
      .select('id, guest_uid, reading_question')
      .eq('guest_uid', guestUID)
      .limit(1);

    if (checkError) {
      console.error('Error checking for session:', checkError);
      alert('An error occurred while checking the session.');
      return;
    }

    console.log('Found existing sessions:', existingSession);
    console.log('First session full data:', JSON.stringify(existingSession[0], null, 2));

    // Also check what sessions exist for debugging
    const { data: allSessions, error: allError } = await supabase
      .from('guest_sessions')
      .select('*')
      .eq('guest_uid', guestUID)
      .limit(1);

    console.log('Full session data for this guestUID:', allSessions);

    let result;
    if (existingSession && existingSession.length > 0) {
      // Update existing session using the specific ID
      const sessionId = existingSession[0].id;
      console.log('Updating existing session with final data');
      console.log('Session ID:', sessionId);
      console.log('Generated uniqueId:', uniqueId);
      console.log('Final step data length:', finalStepData.length);
      
      // Try using upsert instead of update - this might work better with RLS
      console.log('Attempting upsert to save reading_map_url and step_data');
      result = await supabase.from('guest_sessions')
        .upsert({
          id: sessionId, // Include the ID to update existing row
          guest_uid: guestUID,
          reading_map_url: uniqueId,
          step_data: finalStepData,
          reading_type: selectedReadingType,
          reading_question: readingQuestion,
        }, {
          onConflict: 'guest_uid' // Use guest_uid as the conflict resolution key
        })
        .select();

      console.log('Upsert result:', result);

      // If upsert failed, try regular update as fallback
      if (result.error || !result.data || result.data.length === 0) {
        console.log('Upsert failed, trying regular update...');
        result = await supabase.from('guest_sessions')
          .update({
            reading_map_url: uniqueId,
            step_data: finalStepData,
          })
          .eq('id', sessionId)
          .select();
        
        console.log('Update result:', result);

        // If update also fails, we still have the uniqueId - just can't persist to DB
        if (result.error || !result.data || result.data.length === 0) {
          console.warn('Database save failed - reading_map_url not persisted:', uniqueId);
          console.warn('Session will work via navigation state, but URL sharing may not work');
        }
      }
    } else {
      // Create new session with all data
      console.log('Creating new session with final data');
      console.log('Generated uniqueId:', uniqueId);
      
      result = await supabase.from('guest_sessions').insert([
        {
          guest_uid: guestUID,
          reading_map_url: uniqueId,
          reading_type: selectedReadingType,
          reading_question: readingQuestion,
          step_data: finalStepData,
        },
      ]).select(); // Add select to see what was inserted

      console.log('Insert result:', result);
    }

    if (result.error) {
      console.error('Error finalising reading:', result.error);
      alert('An error occurred while saving the session.');
      return;
    }

    // Verify the reading_map_url was saved
    const { data: verifyData, error: verifyError } = await supabase
      .from('guest_sessions')
      .select('reading_map_url')
      .eq('guest_uid', guestUID)
      .limit(1);

    console.log('Verification - saved reading_map_url:', verifyData);
    if (verifyError) {
      console.error('Error verifying save:', verifyError);
    }

    // Always pass session data through navigation state to ensure map page has it
    // This works even if database fetch fails later
    navigate('/finalise', { 
      state: { 
        guest_uid: guestUID, 
        reading_map_url: uniqueId,
        session_data: {
          guest_uid: guestUID,
          reading_type: selectedReadingType,
          reading_question: readingQuestion,
          step_data: finalStepData,
          reading_map_url: uniqueId
        }
      } 
    });
  } catch (err) {
    console.error('Unexpected error during finalisation:', err);
  }
};

  
  return (
    <div className="home-container p-10 max-w-md mx-auto">
      {/* Top right controls container */}
      <div className="absolute top-6 right-6 flex items-center gap-3">
        <DarkModeToggle />
        <button
          onClick={handleExit}
          className="p-2 rounded-md bg-white/50 dark:bg-neutral-800/50 focus:outline-none"
          aria-label="Exit Reading Session"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-4 w-4 text-neutral-400"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m9 9 6-6m0 0 6 6m-6-6v12a6 6 0 0 1-12 0v-3"
            />
          </svg>
        </button>
      </div>
      
      <a href="/"><img alt="TarotMap" src="/favicon.ico" className="mx-auto h-10 w-auto mb-8" /></a>
       <h2 className="text-center text-l font-regular text-neutral-600 dark:text-[#D4D4D4] mb-2">
         {selectedReadingType}</h2>
       <div className=" mt-0 font-normal relative">
       <h2 className="text-center text-l font-semibold text-slate-700 mb-6"> Step {currentStep + 1}</h2></div>
  
   {/* Sticky Tab for Question */}
  
    <button
      onClick={() => setIsQuestionModalOpen(true)}
     className="fixed top-24 left-0 p-2 rounded-r-lg rounded-l-none bg-neutral-600/40 dark:bg-neutral-700/80 shadow-lg focus:ring-0 focus:ring-neutral-400 opacity-60 hover:opacity-100 transition-opacity z-10"
      aria-label="Reading question">
      <svg className="w-5 h-5" fill="none" viewBox="0 0 85.501 82.212">
                 <path d="M42.75,75.635c21.792,0,39.462-16.197,39.462-36.173S64.542,3.288,42.75,3.288,3.288,19.485,3.288,39.462c0,9.225,3.766,17.639,9.966,24.028,1.894,1.96,3.245,4.56,2.569,7.195-.739,2.881-2.123,5.558-4.047,7.827,1.54.277,3.101.414,4.665.412,5.621,0,10.83-1.763,15.105-4.766,3.552.965,7.314,1.478,11.203,1.478Z" className="stroke-neutral-600 dark:stroke-[#D4D4D4] fill-none stroke-[6.577px]" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M45.324,60.255c0,1.925-1.55,3.569-3.522,3.569-1.925,0-3.569-1.597-3.569-3.522,0-1.972,1.597-3.569,3.522-3.569h.047c1.972,0,3.522,1.596,3.522,3.522Z" className="fill-neutral-600 dark:fill-[#D4D4D4]"/>
        <path d="M56.715,30.406c0,3.85-1.69,7.513-4.789,10.189-1.033.939-2.207,1.69-3.522,2.348-1.784.845-3.897,2.535-3.897,4.273v3.052c0,1.972-1.597,3.522-3.522,3.522-1.972,0-3.522-1.55-3.522-3.522v-3.052c0-4.273,2.911-8.217,7.888-10.612.704-.376,1.362-.798,1.925-1.268,1.55-1.362,2.395-3.146,2.395-4.93,0-1.831-.845-3.615-2.395-4.93-3.428-3.005-9.203-3.005-12.631,0-1.502,1.268-3.709,1.127-4.977-.329-1.315-1.503-1.127-3.709.329-4.977,6.151-5.4,15.777-5.4,21.928,0,3.099,2.676,4.789,6.339,4.789,10.236Z" className="fill-neutral-600 dark:fill-[#D4D4D4]"/>
      </svg>
    </button>
  
  {/* Reading Question Modal */}
  
  <Dialog open={isQuestionModalOpen} onClose={() => {}}>
    <div className="fixed inset-0 bg-gray-500/30 dark:bg-[#1a1a1a]/90 backdrop-blur transition-opacity flex items-center justify-center z-10">
      <div className="bg-white dark:bg-[#2F2F2F] shadow-sm sm:rounded-lg">
        <div className="px-12 py-5 sm:p-10">
          <h3 className="text-base font-semibold text-neutral-900 dark:text-[#D4D4D4]">Reading focus</h3>
          <div className="mt-2 max-w-md text-sm text-neutral-500 dark:text-gray-200">
            <p>
            Before you begin, clarify a question for this reading.
            </p>
            </div>
      <div>
              <textarea
                 value={readingQuestion}
                 onChange={(e) => setReadingQuestion(e.target.value)}
                 placeholder="Record the question here"
                 className="border-b-neutral-200 border-transparent focus:border-neutral-500 focus:ring-neutral-500/20 focus:ring-1 rounded-md focus:border-transparent w-full mt-8 resize-none text-base text-neutral-900 dark:text-[#E7E7E7] dark:bg-[#3A3A3A] placeholder:text-neutral-900/60 dark:placeholder:text-[#D4D4D4]/60 sm:text-sm/6"
                defaultValue={''}
                rows={3}            />
          </div>
           <button
                  onClick={handleSaveQuestionAndCloseModal}
                   className="rounded-full bg-transparent text-neutral-900 dark:text-[#D4D4D4] text-base font-regular ring-1 ring-neutral-900 dark:ring-[#D4D4D4] hover:bg-neutral-900 hover:text-white dark:hover:bg-white dark:hover:text-neutral-900 hover:ring-neutral-900 dark:hover:ring-white px-3 py-2 mt-6"
                >
                  Continue
              </button>
            </div>
          </div>
        </div>
      </Dialog>
      
  
  
       {/* Step Details */}
       <div className="text-left text-neutral-500 mb-6">
         <p className="text-sm dark:text-gray-200"> Action: {stepAction || 'Step not available'}</p></div>
        <div><p className="text-stone-400 text-sm mb-10"> Meaning: {stepMeaning || ''}</p>
         </div>
         
  
       {/* Suit Selection */}
       <div className="font-normal relative">
         <p className="text-sm font-semibold mb-4">Card suit drawn:</p> 
          <div className="mt-4 mb-4 grid grid-cols-5 gap-5">
           {Object.keys(cardDetails).map((suitKey) => {
             const { name } = cardDetails[suitKey];
             const iconComponent = {
               wands: WandsIcon,
               rods: WandsIcon,
               cups: CupsIcon,
               swords: SwordsIcon,
               pentacles: PentaclesIcon,
               majorArcana: MajorArcanaIcon,
             }[suitKey] || WandsIcon;
             return (
               <button
                 key={suitKey}
                 onClick={() => handleSuitSelection(suitKey)}
                 className={`suit-icon p-3 rounded-lg bg-neutral-200/20 dark:bg-neutral-700/40 transition-colors`}
                 aria-label={`Select ${name}`}
               >
                 {iconComponent && React.createElement(iconComponent, {
                   className: `flex w-full items-center max-h-8 max-w-8 mx-auto ${selectedSuit === suitKey ? 'text-neutral-900 dark:text-[#D4D4D4]' : 'text-neutral-900/60 dark:text-[#D4D4D4]/60'}`
                 })}
               </button>
             );
           })}
         </div>
       </div>
  
  {/* Card Selection */}
  
       {selectedSuit && (
         <Listbox value={selectedCard || ''} onChange={handleCardSelection}>
           {({ open }) => (
             <div className="relative mb-10">
               <Listbox.Button className="w-full rounded-md bg-white dark:bg-[#2F2F2F] border border-neutral-400 dark:border-neutral-600 py-3 pl-4 pr-10 text-left text-neutral-900 dark:text-[#D4D4D4] focus:outline-none focus:ring-2 focus:ring-neutral-500/30 focus:border-neutral-500/30 transition-all">
                 {selectedCard || <span className="text-neutral-900/60 dark:text-[#D4D4D4]/60">Select a card</span>}
               </Listbox.Button>
               <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#2F2F2F] border border-neutral-400 dark:border-neutral-600 py-1 text-base shadow-lg ring-1 ring-black/10 focus:outline-none sm:text-sm">
                 {cardDetailsForSuit.map((card) => (
                   <Listbox.Option
                     key={card.name}
                     value={card.name}
                     className={({ active, selected }) =>
                       `cursor-pointer select-none relative py-2 pl-4 pr-10 
                        ${selected ? 'bg-neutral-100/60 text-neutral-900 dark:bg-neutral-700/40 dark:text-[#D4D4D4]' : active ? 'bg-neutral-50 text-neutral-900 dark:bg-gray-700 dark:text-white' : 'text-neutral-900 dark:text-[#D4D4D4]'}`
                     }
                   >
                     {card.name}
                   </Listbox.Option>
                 ))}
               </Listbox.Options>
             </div>
           )}
         </Listbox>
       )}
  
  
       {/* Reverse Toggle */}
       {selectedCard && (
         <div className="font-normal relative mb-6">
           <div className="flex items-center space-x-3">
                         <button
              onClick={() => setIsReversed(!isReversed)}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-0"
              style={{
                border: 'none',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
              }}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                  isReversed ? 'bg-neutral-600' : 'bg-neutral-400'
                } ${isReversed ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
            <span className={`text-xs transition-colors ${isReversed ? 'text-neutral-600' : 'text-neutral-400'}`}>Reverse</span>
           </div>
         </div>
       )}
  
  
        {/* Card Details */}
        {selectedCard && cardDetailsForSelectedCard && (
         <div className="font-normal relative">
           <p className="text-sm font-semibold">You selected:</p>
           <div
             className="card-details font-regular text-sm mt-4 mb-10 p-10 border border-neutral-600 rounded-md relative overflow-hidden shadow-xl bg-stone-100 dark:bg-[#2F2F2F]"
             >
            
             {/* Top-left and Top-right Suit Icons */}
             <div className="absolute top-6 left-8 flex flex-col items-center">
               {(() => {
                 const iconComponent = {
                   wands: WandsIcon,
                   rods: WandsIcon,
                   cups: CupsIcon,
                   swords: SwordsIcon,
                   pentacles: PentaclesIcon,
                   majorArcana: MajorArcanaIcon,
                 }[selectedSuit] || WandsIcon;
                 return iconComponent ? React.createElement(iconComponent, { className: 'w-10 h-10 m-4 text-neutral-700 dark:text-[#D4D4D4]', strokeWidth: 0.8 }) : null;
               })()}
             </div>
             <div className="absolute top-6 right-8 flex flex-col items-center">
               {(() => {
                 const iconComponent = {
                   wands: WandsIcon,
                   rods: WandsIcon,
                   cups: CupsIcon,
                   swords: SwordsIcon,
                   pentacles: PentaclesIcon,
                   majorArcana: MajorArcanaIcon,
                 }[selectedSuit] || WandsIcon;
                 return iconComponent ? React.createElement(iconComponent, { className: 'w-10 h-10 m-4 text-neutral-700 dark:text-[#D4D4D4]', strokeWidth: 0.8 }) : null;
               })()}
             </div>
  
  
             {/* Center Display for Court, Numbered Cards, and Major Arcana */}
             <div className="flex justify-center items-center h-40 mt-6">
               {[
                 "King",
                 "Queen",
                 "Knight",
                 "Page"
               ].includes(selectedCard) ? (
                 <img src={courtCardImages[selectedCard]} alt={`${selectedCard} icon`} className="w-40 h-40 mt-8" />
               ) : (
                 <span className="text-5xl text-neutral-700 dark:text-[#D4D4D4] font-bold text-center">
                   {numberMapping[selectedCard] || cardDetailsForSelectedCard.name}
                 </span>
               )}
             </div>
  
  
             {/* Card Name and Description */}
              <p className="text-lg text-neutral-900 dark:text-[#D4D4D4] font-semibold mt-6 text-center">
               {cardDetailsForSelectedCard.name} of {cardDetails[selectedSuit].name}
             </p>
             <p className={`mt-2 ${isReversed ? 'text-stone-500' : 'text-neutral-900 dark:text-white'}`}> 
               {cardDetailsForSelectedCard.meaning}
             </p>
             <p className={`text-sm italic mt-2 ${isReversed ? 'text-neutral-900 dark:text-white' : 'text-stone-500'}`}> 
               Reverse: {cardDetailsForSelectedCard.reverse}
             </p>
           </div>
         </div>
       )}
  
  
       {/* Note-Taking Section */}
        <textarea
         value={notes[currentStep] || ''}
         onChange={handleNoteChange}
         placeholder="Add notes here"
          className="text-base w-full mb-6 p-3 border border-neutral-600 dark:border-neutral-700 rounded-md ring-0 focus:ring-0 focus:border-neutral-600 dark:focus:border-neutral-600 bg-white dark:bg-[#2F2F2F] dark:text-[#E7E7E7] placeholder:text-neutral-900 dark:placeholder:text-[#D4D4D4] hover:bg-neutral-100 dark:hover:bg-[#2F2F2F]"
       />
  
  
   {/* Navigation Buttons */}
  <div className="navigation-buttons flex justify-between mb-10">
    {/* Previous Button */}
    <button
      onClick={handlePreviousStep}
      disabled={currentStep === 0}
      className={`p-2 w-1/2 mr-2 ${
        currentStep === 0
          ? 'rounded-full bg-transparent text-neutral-900 dark:text-[#D4D4D4] ring-1 ring-neutral-900 dark:ring-[#D4D4D4] cursor-not-allowed'
          : 'rounded-full bg-transparent text-neutral-900 dark:text-[#D4D4D4] text-base font-regular ring-1 ring-neutral-900 dark:ring-[#D4D4D4] hover:bg-neutral-900 hover:text-white dark:hover:bg-white dark:hover:text-neutral-900 hover:ring-neutral-900 dark:hover:ring-white'
      }`}
    >
      Previous
    </button>

 {/* Next/Finalise Button */}
<button
  onClick={currentStep === steps.length - 1 ? handleFinaliseReading : handleNextStep}
  disabled={!selectedSuit || !selectedCard}
  className={`p-2 rounded w-1/2 ml-0 ${
    !selectedSuit || !selectedCard
      ? 'rounded-full bg-transparent text-neutral-900 dark:text-[#D4D4D4] ring-1 ring-neutral-900 dark:ring-[#D4D4D4] cursor-not-allowed'
      : 'rounded-full bg-transparent text-neutral-900 dark:text-[#D4D4D4] text-base font-regular ring-1 ring-neutral-900 dark:ring-[#D4D4D4] hover:bg-neutral-900 hover:text-white dark:hover:bg-white dark:hover:text-neutral-900 hover:ring-neutral-900 dark:hover:ring-white'
  }`}
>
  {currentStep === steps.length - 1 ? 'Finalise Reading' : 'Next'}
</button>
</div>

  

  
  
          {/* Sticky Map Icon */}
          <button
         onClick={() => setIsMapOpen(true)}
         className="fixed top-6 left-0 p-2 rounded-r-lg rounded-l-none bg-neutral-600/40 dark:bg-neutral-700/80 shadow-lg focus:ring-0 focus:ring-neutral-400 opacity-60 hover:opacity-100 transition-opacity z-10"
         aria-label="Open Step Map"
       >
         <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5">
           <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" className="stroke-neutral-600 dark:stroke-[#D4D4D4]" />
         </svg>
       </button>
  
  
       {/* Map Panel */}
       <Dialog open={isMapOpen} onClose={() => setIsMapOpen(false)} className="relative z-10">
         {/* Backdrop */}
         <div className="fixed inset-0 bg-gray-500/30 dark:bg-[#1a1a1a]/90 transition-opacity" aria-hidden="true" />
  
  
         {/* Centered Panel */}
         <div className="fixed inset-0 flex items-center justify-center">
           <Dialog.Panel className="relative w-11/12 max-w-lg bg-white dark:bg-[#2F2F2F] shadow-xl rounded-lg">
             {/* Panel Header */}
             <div className="flex items-center justify-between p-6">
               <Dialog.Title className="text-m font-medium text-neutral-900 dark:text-[#D4D4D4]">
                 Step {currentStep + 1} Position
               </Dialog.Title>
               <button
                 onClick={() => setIsMapOpen(false)}
                 className="rounded-md text-neutral-900/60 dark:text-[#D4D4D4]/60 hover:text-neutral-900 dark:hover:text-[#D4D4D4] focus:ring-neutral-500/20 focus:outline-none"
                 aria-label="Close panel"
               >
                 <XMarkIcon className="h-6 w-6" />
               </button>
             </div>
  
  
             {/* Panel Content */}
             <div className="relative px-4 py-6">
               <img src={[stepMap]}
                 alt={`Map for Step ${currentStep +1}`}
                 className="w-full h-auto rounded-md bg-neutral-100 dark:bg-[#3A3A3A] p-2"
               />
             </div>
           </Dialog.Panel>
         </div>
       </Dialog>
     </div>
   );
  };
  
  export default ReadingSession;